import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface ContentTranslationType {
  id: number;
  type: string;
  name: string;
  isDeleted: boolean;
  effectiveDate: Date;
  contentContains: string;
  codeLength: number;
  isAlphanumeric: boolean;
  operationType: "REPLACE" | "PARSE_DIGITS" | "FORMAT_DIGITS";
  replaceValue: string;
  replaceWithValue: string;
  separateDigitsBy: string;
  addBeforeExtractedCode: string;
  addAfterExtractedCode: string;
  operatorId: number;
  routingPlanId: string;
  position: string;
  providerId: string;
  createdBy: string;
  contentExtract?: string;
  version?: number;
  updatedBy?: string;
  createdDate: Date;
  historyTimestamp: Date;
}

export interface ContentHistory {
  id: string;
  snapshot: ContentTranslationType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export const exportContentTranslation = async (
  params: Params<ContentTranslationType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/content-translation/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "ContentTranslation.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching ContentTranslation :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchContentTranslations(
  params: Params<ContentTranslationType>
): Promise<IPageable<ContentTranslationType>> {
  try {
    const response = await axiosInstance.get("/content-translation", {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchContentTranslation(
  id: ContentTranslationType["id"]
): Promise<ContentTranslationType> {
  try {
    const response = await axiosInstance.get(`/content-translation/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addContentTranslationApi = async (
  data: Partial<ContentTranslationType>
) => {
  try {
    const response = await axiosInstance.post("/content-translation", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Content Routing."
    );
  }
};
export const ContentTranslationBulkApi = async (
  data: Partial<ContentTranslationType>[]
) => {
  try {
     const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/content-translation", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Content Routing."
    );
  }
};

export const ContentTranslationCreateAllApi = async (
  data: Partial<ContentTranslationType>[]
) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.post("/content-translation/all", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Content Routing."
    );
  }
};

export const editContentTranslationApi = async (
  id: string,
  data: Partial<ContentTranslationType>
) => {
  try {
    const response = await axiosInstance.put(
      `/content-translation/${id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the Content Routing."
    );
  }
};

export const getContentTranslationById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/content-translation/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the Content Routing."
    );
  }
};

export const deleteContentTranslationApi = async (
  ids: string[]
): Promise<void> => {
  try {
    await axiosInstance.delete(`/content-translation`, { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the Content Routing."
    );
  }
};

export const useContentTranslationQuery = (
  params: Params<ContentTranslationType>
): UseQueryResult<IPageable<ContentTranslationType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["contentTranslation", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchContentTranslations(effectiveParams),
  });
};

export async function fetchContentHistory(
  id: ContentTranslationType["id"],
  params: Params<ContentTranslationType> = {}
): Promise<IPageable<ContentTranslationType>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, {
      params: { mainId: id , sort : "historyTimestamp,desc", ...params },
    });
   const data = response.data;
   return {
      ...data,
            content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Content history for ID ${id}.`
    );
  }
}
